import type { TFunction } from 'i18next';
import type { Product } from '../types';

/**
 * BLU-1527 S3: who the back-up stock comes from, as the `owner` of the lines
 * below. A 3PL's back-up names the 3PL; an own substitute names the
 * substitute garment ("substitute TEE2-L-BLK"); a rolled-up row with both
 * names the two ("Thread Lab and substitute TEE2-L-BLK").
 */
export function backupSourceName(
  t: TFunction<'inventory'>,
  p: Pick<Product, 'fallbackOwnerName' | 'fallbackKind' | 'fallbackSubstituteSku'>,
): string {
  const owner = p.fallbackOwnerName ?? '';
  const sku = p.fallbackSubstituteSku;
  if (!sku) return owner;
  return p.fallbackKind === 'own'
    ? t('sharedSkus.substituteSource', { sku })
    : t('sharedSkus.backupAndSubstituteSource', { owner, sku });
}

/**
 * BLU-1458: the split behind a figure that carries back-up stock, for the
 * hover title and screen readers: "7 from Thread Lab, 0 owned by Mini Zali".
 * `own` is the row's or cell's own figure as is (negative stock is allowed).
 * Without the caller's org name, the sentence that names only the 3PL.
 */
export function backupSplitLine(
  t: TFunction<'inventory'>,
  args: { backup: number; owner: string; own: number; ownOrgName: string | null | undefined },
): string {
  const { backup, owner, own, ownOrgName } = args;
  return ownOrgName
    ? t('sharedSkus.backupSplit', { backup, owner, own, org: ownOrgName })
    : t('sharedSkus.backupSplitNoOrg', { backup, owner, own });
}

/**
 * BLU-1268: the split behind a row whose back-up is the 3PL's just-in-time
 * blank, shown under the Just in time mark: "Just in time from Thread Lab,
 * 3 owned by Mini Zali". The back-up has no figure, so only the own one shows.
 */
export function backupJustInTimeLine(
  t: TFunction<'inventory'>,
  args: { owner: string; own: number; ownOrgName: string | null | undefined },
): string {
  const { owner, own, ownOrgName } = args;
  return ownOrgName
    ? t('sharedSkus.backupJustInTime', { owner, own, org: ownOrgName })
    : t('sharedSkus.backupJustInTimeNoOrg', { owner, own });
}
